<template>
  <div class="item-details-page">
    <section class="page-header fade-in">
      <div class="header-left">
        <button class="btn btn-light" @click="goBack">
          <i class="bi bi-arrow-left"></i>
          <span>Retour</span>
        </button>
        <div>
          <h1 class="page-title">Détails de l'article</h1>
          <p class="page-subtitle" v-if="item">
            {{ item.fullName || item.name || item.reference }}
          </p>
        </div>
      </div>
      <div class="header-actions" v-if="item">
        <button
          v-if="!hasBarcode"
          @click="generateBarcode"
          class="btn btn-primary"
          :disabled="generatingBarcode"
        >
          <i class="bi bi-upc-scan"></i>
          <span v-if="!generatingBarcode">Générer code-barres</span>
          <span v-else>Génération...</span>
        </button>
        <button
          v-if="hasBarcode"
          @click="printBarcode"
          class="btn btn-success"
          :disabled="printingBarcode"
        >
          <i class="bi bi-printer"></i>
          <span v-if="!printingBarcode">Imprimer code-barres</span>
          <span v-else>Impression...</span>
        </button>
        <button
          v-if="hasBarcode"
          @click="downloadBarcodeJpg"
          class="btn btn-info"
          :disabled="downloadingBarcodeJpg"
        >
          <i class="bi bi-download"></i>
          <span v-if="!downloadingBarcodeJpg">Télécharger JPG</span>
          <span v-else>Téléchargement...</span>
        </button>
        <button class="btn btn-secondary" @click="editItem">
          <i class="bi bi-pencil"></i>
          <span>Modifier</span>
        </button>
        <button class="btn btn-danger" @click="deleteItem">
          <i class="bi bi-trash"></i>
          <span>Supprimer</span>
        </button>
      </div>
    </section>

    <section v-if="!loading && item" class="fade-in">
      <div class="info-grid">
        <div class="info-card">
          <h2 class="section-title">Informations générales</h2>
          <div class="info-line">
            <span class="label">Nom complet</span>
            <span class="value">{{ item.fullName || item.name || "—" }}</span>
          </div>
          <div class="info-line">
            <span class="label">Code</span>
            <span class="value">{{ item.code || "—" }}</span>
          </div>
          <div class="info-line">
            <span class="label">Code-barres</span>
            <span class="value">{{ item.barCode || "—" }}</span>
          </div>
          <div class="info-line">
            <span class="label">Référence</span>
            <span class="value badge">{{ item.reference || "—" }}</span>
          </div>
          <div class="info-line">
            <span class="label">Prix TTC</span>
            <span class="value price-value">{{
              formatPrice(item.priceTTC)
            }}</span>
          </div>
        </div>

        <div class="info-card" v-if="item.description">
          <h2 class="section-title">Description</h2>
          <div class="info-line">
            <span class="value">{{ item.description || "—" }}</span>
          </div>
        </div>
      </div>
    </section>

    <div v-else-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>Chargement de l'article...</p>
    </div>

    <div v-else class="state-empty">
      <i class="bi bi-box-seam"></i>
      <p>Article introuvable ou supprimé.</p>
      <button class="btn btn-primary" @click="goToList">
        <i class="bi bi-box-seam"></i>
        <span>Voir les articles</span>
      </button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "ItemShow",
  data() {
    return {
      loading: true,
      generatingBarcode: false,
      printingBarcode: false,
      downloadingBarcodeJpg: false,
    };
  },
  computed: {
    ...mapGetters("item", {
      item: "getItem",
    }),
    reference() {
      return this.$route.params.reference;
    },
    hasBarcode() {
      if (!this.item || !this.item.description) return false;
      return /Code-barres:\s*[^\n\r]+/i.test(this.item.description);
    },
  },
  methods: {
    formatPrice(price) {
      if (price == null) return "—";
      return new Intl.NumberFormat("fr-FR", {
        style: "currency",
        currency: "MAD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(price);
    },
    goBack() {
      this.$router.back();
    },
    goToList() {
      this.$router.push({ name: "items-list" });
    },
    editItem() {
      this.$router.push({
        name: "items-edit",
        params: { reference: this.reference },
      });
    },
    async deleteItem() {
      const confirmed = window.confirm(
        "Êtes-vous sûr de vouloir supprimer cet article ?"
      );
      if (!confirmed) return;
      try {
        await this.$store.dispatch("item/destroy", this.reference);
        this.$store.dispatch("notification/store", {
          status: "success",
          message: "Article supprimé avec succès.",
        });
        this.goToList();
      } catch (error) {
        console.error("Error deleting item", error);
        this.$store.dispatch("notification/store", {
          status: "error",
          message: "Impossible de supprimer l'article.",
        });
      }
    },
    async fetchItem() {
      this.loading = true;
      try {
        await this.$store.dispatch("item/show", this.reference);
      } catch (error) {
        console.error("Error fetching item", error);
        this.$store.dispatch("notification/store", {
          status: "error",
          message: "Article introuvable.",
        });
      } finally {
        this.loading = false;
      }
    },
    async generateBarcode() {
      if (!this.item || !this.item.reference) return;

      this.generatingBarcode = true;
      try {
        await this.$store.dispatch("item/generateBarcode", this.item.reference);
        // Refresh item data
        await this.fetchItem();
      } catch (error) {
        console.error("Error generating barcode:", error);
      } finally {
        this.generatingBarcode = false;
      }
    },
    async printBarcode() {
      if (!this.item || !this.item.reference) return;

      this.printingBarcode = true;
      try {
        await this.$store.dispatch("item/printBarcode", this.item.reference);
      } catch (error) {
        console.error("Error printing barcode:", error);
      } finally {
        this.printingBarcode = false;
      }
    },
    async downloadBarcodeJpg() {
      if (!this.item || !this.item.reference) return;

      this.downloadingBarcodeJpg = true;
      try {
        await this.$store.dispatch(
          "item/downloadBarcodeJpg",
          this.item.reference
        );
      } catch (error) {
        console.error("Error downloading barcode JPG:", error);
      } finally {
        this.downloadingBarcodeJpg = false;
      }
    },
  },
  mounted() {
    this.fetchItem();
  },
  beforeDestroy() {
    this.$store.dispatch("item/reset");
  },
};
</script>

<style scoped>
.item-details-page {
  min-height: 100vh;
  background: var(--bg-secondary);
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.header-left {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.page-title {
  margin: 0;
  font-size: 2.25rem;
  font-weight: 700;
}

.page-subtitle {
  margin: 0;
  color: var(--text-secondary);
  font-weight: 500;
}

.header-actions {
  display: flex;
  gap: 0.75rem;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
}

.info-card {
  background: white;
  border-radius: var(--radius-lg);
  padding: 1.5rem;
  box-shadow: var(--shadow-sm);
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.section-title {
  margin: 0;
  font-size: 1.25rem;
  color: var(--text-primary);
}

.info-line {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1rem;
  background: var(--bg-secondary);
  border-radius: var(--radius-md);
}

.label {
  color: var(--text-light);
  font-weight: 600;
}

.value {
  color: var(--text-primary);
  font-weight: 500;
  text-align: right;
}

.value.badge {
  background: rgba(63, 81, 181, 0.1);
  color: var(--primary-color);
  padding: 0.25rem 0.75rem;
  border-radius: 999px;
  font-size: 0.875rem;
}

.value.price-value {
  font-weight: 600;
  color: #059669;
  font-size: 1.05rem;
}

.loading-state,
.state-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  min-height: 60vh;
  color: var(--text-secondary);
}

.state-empty i {
  font-size: 3rem;
  color: var(--primary-color);
}

.spinner {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 4px solid var(--border-color);
  border-top-color: var(--primary-color);
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  border: none;
  transition: all 0.2s ease;
}

.btn-light {
  background: #f8fafc;
  color: #475569;
  border: 1px solid #e2e8f0;
}

.btn-light:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
}

.btn-secondary {
  background: #f8fafc;
  color: #475569;
  border: 1px solid #e2e8f0;
}

.btn-secondary:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
}

.btn-danger {
  background: #ef4444;
  color: white;
}

.btn-danger:hover {
  background: #dc2626;
}

.btn-primary {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.btn-primary:hover {
  box-shadow: 0 6px 16px rgba(59, 130, 246, 0.4);
}

@media (max-width: 1024px) {
  .info-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .item-details-page {
    padding: 1rem;
  }

  .info-grid {
    grid-template-columns: 1fr;
  }

  .header-actions {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
